import { VOTE_SELECTOR_RADIUS } from "@/appInterface/windows/VoteWindow/VoteScreen";
import { canv } from "@/canv/global";
import {
    generateUID,
    getBoolProperty,
    getNumberProperty,
    getStringProperty,
    isBrowser,
} from "@/canv/utils";
import { getViewport, sortByIndexByParent } from "@/canvasController/helpers";
import { CONSTRUCTORS_WITHOUT_ANCHORS } from "@/constants";
import type { ElementBase } from "@/elementBase";
import type { SearchTextObject } from "@/elementBase/types";
import { ArrowWidget } from "@/elements/arrow";
import { CommentWidget } from "@/elements/comment";
import { DocumentViewerWidget } from "@/elements/documentViewer";
import { EmojiWidget } from "@/elements/emoji";
import { frameWidget } from "@/elements/frame";
import { GifWidget } from "@/elements/gif";
import { ImageWidget } from "@/elements/image";
import { PresentationWidget } from "@/elements/Presentation/Presentation";
import { ShapeWidget } from "@/elements/shape";
import { SketchWidget } from "@/elements/sketch";
import { StickerWidget } from "@/elements/sticker";
import { TableWidget } from "@/elements/table/table";
import { TextWidgetV2 } from "@/elements/text/textV2";
import { TreeNodeWidget } from "@/elements/treeNode";
import { generateKeyBetween } from "@/helpers/fractional-indexing";
import type { EntityBaseState } from "@/shared/types/entityState";
import type { FixType } from "@/shared/types/fixType";
import type { WidgetConstructorName } from "@/shared/types/widgetConstructorName";
import { getScale, isInside, queryAreaByGroup } from "@/utils";
import type { Area, ArrowData, ArrowPlace, GroupStateByFixState, RatioData, Rect } from "./types";

export class Group {
    constructor(isItemWrapper = false, isGhost = false) {
        this.parent = null;
        this.indexByParent = generateKeyBetween(null, null);
        this.UID = generateUID();
        this.itemWrapper = isItemWrapper;
        this.angle = 0;
        this.isGhost = isGhost;

        this.updateDims();
    }

    updateDims() {
        if (this.children.length == 0) return;

        if (this.isItemWrapper()) {
            const children = this.children as ElementBase[];

            this.width =
                children[0].baseHeight * Math.abs(Math.sin(this.angle * (Math.PI / 180))) +
                children[0].baseWidth * Math.abs(Math.cos(this.angle * (Math.PI / 180)));
            this.height =
                children[0].baseHeight * Math.abs(Math.cos(this.angle * (Math.PI / 180))) +
                children[0].baseWidth * Math.abs(Math.sin(this.angle * (Math.PI / 180)));
            this.startX = children[0].baseStartX + children[0].baseWidth / 2 - this.width / 2;
            this.startY = children[0].baseStartY + children[0].baseHeight / 2 - this.height / 2;

            if (!this.isGhost && this.isGroupOnScreen()) {
                canv.canvasController.hashGrid?.update(this);
            }
        } else {
            const children = this.children as Group[];

            let left = children[0].startX;
            let right = children[0].startX + children[0].width;
            let top = children[0].startY;
            let bottom = children[0].startY + children[0].height;

            for (let i = 0; i < children.length; i++) {
                if (children[i].startX < left) {
                    left = children[i].startX;
                }

                if (children[i].startX + children[i].width > right) {
                    right = children[i].startX + children[i].width;
                }

                if (children[i].startY < top) {
                    top = children[i].startY;
                }

                if (children[i].startY + children[i].height > bottom) {
                    bottom = children[i].startY + children[i].height;
                }
            }

            this.startX = left;
            this.startY = top;
            this.width = right - left;
            this.height = bottom - top;
        }

        if (!isBrowser()) return;

        for (let i = 0; i < this.arrows.length; i++) {
            this.updateArrowPosition(this.arrows[i]);
        }
    }

    private updateFromPendingTextResizeBounds(child: ElementBase | null) {
        if (!(child instanceof TextWidgetV2) || !this.isItemWrapper()) {
            return;
        }

        const bounds = child.syncPendingResizeGeometryForTransform();
        if (!bounds) {
            return;
        }

        const angle = this.angle * (Math.PI / 180);
        const width =
            bounds.height * Math.abs(Math.sin(angle)) + bounds.width * Math.abs(Math.cos(angle));
        const height =
            bounds.height * Math.abs(Math.cos(angle)) + bounds.width * Math.abs(Math.sin(angle));

        this.width = width;
        this.height = height;
        this.startX = bounds.startX + bounds.width / 2 - width / 2;
        this.startY = bounds.startY + bounds.height / 2 - height / 2;

        if (!this.isGhost && this.isGroupOnScreen()) {
            canv.canvasController.hashGrid?.update(this);
        }

        if (this.parent != null) {
            this.parent.bottomTopUpdateDims();
        }
    }

    updateArrowPosition(arrowData: ArrowData) {
        const connPost = this.calcConnectorPosition(arrowData.place, arrowData.share);
        const foundItem = canv.canvasController.FindByUID(arrowData.arrowItemUID);

        if (!foundItem || !connPost || !(foundItem instanceof ArrowWidget)) {
            return;
        }

        if (arrowData.point === "firstPoint") {
            const firstPoint = foundItem.getFirstPoint();

            firstPoint.x = connPost.x;
            firstPoint.y = connPost.y;
        } else {
            const lastPoint = foundItem.getLastPoint();

            lastPoint.x = connPost.x;
            lastPoint.y = connPost.y;
        }

        foundItem.updateDims(false);
    }

    bottomTopUpdateDims() {
        this.updateDims();

        if (this.parent != null) {
            this.parent.bottomTopUpdateDims();
        }
    }

    // вызывается когда собрали branch
    fullUpdateDims() {
        if (this.isItemWrapper()) {
            this.bottomTopUpdateDims();
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].fullUpdateDims();
            }
        }
    }

    addChild(newChild: ElementBase) {
        const children = this.children as ElementBase[];

        children.push(newChild);
        //@ts-ignore   TODO: убереться после переписывания ElementBase на TS
        newChild.parent = this as Group;
        newChild.updateDims();

        this.bottomTopUpdateDims();
    }

    // draws
    showConnectors() {
        canv.ctx.save();

        this.pointerSize = canv.actionManager.getZoomConstant(10);

        if (this.pointerSize < 4) {
            this.pointerSize = 4;
        }

        if (this.pointerSize >= this.width || this.pointerSize >= this.height) {
            this.pointerSize = 4;
        }

        canv.ctx.fillStyle = "white";
        canv.ctx.strokeStyle = "#0B99FF";
        canv.ctx.lineWidth = this.pointerSize / 3;

        canv.ctx.beginPath();
        canv.ctx.arc(
            this.startX + this.width / 2,
            this.startY,
            this.pointerSize / 2,
            0,
            2 * Math.PI,
        );
        canv.ctx.stroke();
        canv.ctx.beginPath();
        canv.ctx.arc(
            this.startX + this.width / 2,
            this.startY,
            this.pointerSize / 2,
            0,
            2 * Math.PI,
        );
        canv.ctx.fill();
        canv.ctx.closePath();

        canv.ctx.beginPath();
        canv.ctx.arc(
            this.startX + this.width,
            this.startY + this.height / 2,
            this.pointerSize / 2,
            0,
            2 * Math.PI,
        );
        canv.ctx.stroke();
        canv.ctx.beginPath();
        canv.ctx.arc(
            this.startX + this.width,
            this.startY + this.height / 2,
            this.pointerSize / 2,
            0,
            2 * Math.PI,
        );
        canv.ctx.fill();
        canv.ctx.closePath();

        canv.ctx.beginPath();
        canv.ctx.arc(
            this.startX + this.width / 2,
            this.startY + this.height,
            this.pointerSize / 2,
            0,
            2 * Math.PI,
        );
        canv.ctx.stroke();
        canv.ctx.beginPath();
        canv.ctx.arc(
            this.startX + this.width / 2,
            this.startY + this.height,
            this.pointerSize / 2,
            0,
            2 * Math.PI,
        );
        canv.ctx.fill();
        canv.ctx.closePath();

        canv.ctx.beginPath();
        canv.ctx.arc(
            this.startX,
            this.startY + this.height / 2,
            this.pointerSize / 2,
            0,
            2 * Math.PI,
        );
        canv.ctx.stroke();
        canv.ctx.beginPath();
        canv.ctx.arc(
            this.startX,
            this.startY + this.height / 2,
            this.pointerSize / 2,
            0,
            2 * Math.PI,
        );
        canv.ctx.fill();
        canv.ctx.closePath();

        canv.ctx.restore();
    }

    showLowOutline() {
        //canv.ctx.save();
        canv.ctx.strokeStyle = "#84c0fb";
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(2.5);
        canv.ctx.strokeRect(this.startX, this.startY, this.width, this.height);
        //canv.ctx.restore();
    }

    shouldDrawHoverBlackout(widget: ElementBase) {
        if (!canv.hoverArea || CONSTRUCTORS_WITHOUT_ANCHORS.includes(widget.constructorName))
            return false;

        const groupArea = {
            x: this.startX,
            y: this.startY,
            width: this.width,
            height: this.height,
        };

        return isInside(groupArea, canv.hoverArea);
    }

    drawHoverBlackout() {
        canv.ctx.save();
        canv.ctx.fillStyle = "rgba(0, 0, 0, 0.1)";
        canv.ctx.fillRect(this.startX, this.startY, this.width, this.height);
        canv.ctx.restore();
    }

    checkGroupPositionOfViewport() {
        if (!this.isGhost && this.isItemWrapper()) {
            if (this.isGroupOnScreen()) {
                if (canv.canvasController.hashGrid?.needToUpdate(this)) {
                    canv.canvasController.hashGrid?.update(this);
                }
            } else {
                canv.canvasController.hashGrid?.remove(this);
            }
        }
    }

    isOverVoteSelector() {
        const pt = canv.actionManager.getActionPoint();
        const r = VOTE_SELECTOR_RADIUS;
        const endX = this.startX + this.width;
        const endY = this.startY + this.height;

        return pt.x >= endX - r && pt.x <= endX + r && pt.y >= endY - r && pt.y <= endY + r;
    }

    drawForVoteAfterDrawElement() {
        if (canv.appInterface.activeVote.isOpen && this.isItemWrapper()) {
            const activeVoteInterface = canv.appInterface.activeVote?.interface;
            const isCandidate = activeVoteInterface?.candidateUIDs?.has(this.children[0].UID);

            if (isCandidate && activeVoteInterface) {
                const { startX, startY, width, height } = this.getVoteDrawData();
                activeVoteInterface.drawBorder({ startX, startY, width, height });
            }
        }

        if (
            canv.appInterface.voteWindow.opened &&
            canv.appInterface.voteWindow.isShowResult &&
            canv.appInterface.voteWindow.selectedResultItemUID &&
            this.isItemWrapper()
        ) {
            const isCandidate = canv.appInterface.voteWindow.widgetUIDs.has(this.children[0].UID);
            if (
                isCandidate &&
                canv.appInterface.voteWindow.selectedResultItemUID === this.children[0].UID
            ) {
                canv.appInterface.voteWindow.interface.drawSelectedBorder(this.getVoteDrawData());
            }
        }

        if (
            canv.canvasController.showVotesResults &&
            canv.appInterface.voteWindow.opened &&
            canv.appInterface.voteWindow.isShowResult &&
            !canv.appInterface.activeVote.isOpen &&
            this.isItemWrapper()
        ) {
            const isCandidate = canv.appInterface.voteWindow.widgetUIDs.has(this.children[0].UID);
            if (isCandidate) {
                canv.appInterface.voteWindow.interface.drawBorder(this.getVoteDrawData());
            }
        }

        canv.ctx.globalAlpha = 1;
    }

    getVoteDrawData() {
        return {
            startX: this.startX,
            startY: this.startY,
            width: this.width,
            height: this.height,
            widgetUID: this.children[0].UID,
        };
    }

    drawForVoteBeforeDrawElement() {
        if (!this.itemWrapper) return;

        if (
            canv.appInterface.voteWindow.opened &&
            canv.appInterface.voteWindow.activeTab === "new"
        ) {
            // Если включена настройка голосования
            if (canv.appInterface.voteWindow.screen?.candidates.has(this.UID)) {
                // Если элемент находится в области голосования
                const isSelected = canv.appInterface.voteWindow.screen?.selectedGroupUIDs.has(
                    this.UID,
                );

                if (!isSelected) {
                    canv.ctx.globalAlpha = 0.5;
                }
            } else {
                // Если элемент не находится в области голосования
                canv.ctx.globalAlpha = 0.5;
            }

            if (canv.appInterface.voteWindow.screen?.needBorder(this)) {
                const ratio = canv.appInterface.voteWindow.screen?.getRatio();
                const padding = 5 / ratio;

                canv.ctx.save();

                canv.ctx.strokeStyle = "rgba(224, 224, 233, 1)";
                canv.ctx.lineWidth = 1 / ratio;
                canv.ctx.beginPath();
                canv.ctx.roundRect(
                    this.startX - padding,
                    this.startY - padding,
                    this.width + 2 * padding,
                    this.height + 2 * padding,
                    [4 / ratio, 4 / ratio, 4 / ratio, 4 / ratio],
                );

                canv.ctx.stroke();
                canv.ctx.restore();
            }
        }
    }

    draw(needToDrawNotItemWrapper = false) {
        this.checkGroupPositionOfViewport();
        this.drawForVoteBeforeDrawElement();

        if (this.children.length === 0) return;

        let anyChildIsSelected = false;

        for (let i = 0; i < this.children.length; i++) {
            if (this.isItemWrapper()) {
                const children = this.children as ElementBase[];
                if (!children[i].shouldDraw()) continue;

                if (this.angle !== 0) {
                    canv.ctx.save();

                    const trX = this.startX + this.width / 2;
                    const trY = this.startY + this.height / 2;

                    canv.ctx.translate(trX, trY);
                    canv.ctx.rotate((-this.angle * Math.PI) / 180);
                    canv.ctx.translate(-trX, -trY);
                }

                children[i].draw();

                if (this.angle !== 0) {
                    canv.ctx.restore();
                }

                if (this.shouldDrawHoverBlackout(children[i])) {
                    this.drawHoverBlackout();
                }
            } else if (needToDrawNotItemWrapper) {
                this.children[i].draw();
            }

            if (canv.canvasController.isInSelected(this.children[i])) anyChildIsSelected = true;
        }

        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");
        const nothingForInteraction =
            cursorTool.isMode("interaction") &&
            canv.canvasController.isInSelected(this) &&
            this.onlyChild()?.nothingWhenInteraction;

        const isInSelectedReason =
            !nothingForInteraction &&
            canv.canvasController.isInSelected(this) &&
            (!this.isItemWrapper() || !this.onlyChild()?.disableOutline) &&
            !cursorTool.isMode("fastDragging");

        if (this.needLowOutline || anyChildIsSelected || isInSelectedReason) {
            this.showLowOutline();
        }

        if (this.needConnectors) {
            this.showConnectors();
        }

        this.drawForVoteAfterDrawElement();
    }

    drawOver() {
        for (let i = 0; i < this.children.length; i++) {
            if (this.isItemWrapper()) {
                const children = this.children as ElementBase[];

                // рисуем тултип в режиме активного голосования
                if (canv.appInterface.activeVote.isOpen) {
                    canv.appInterface.activeVote.drawOver(this);
                }

                if (!children[i].shouldDraw()) continue;
            }

            // Если у нас открыто окно с созданием голосования и в кандидатах находится текущая группа
            if (canv.appInterface.voteWindow.screen?.candidates.has(this.UID)) {
                const isSelected = canv.appInterface.voteWindow.screen?.selectedGroupUIDs.has(
                    this.UID,
                );
                if (isSelected) {
                    canv.ctx.save();
                    const k = 2.5; // получен эмпирическим путем
                    let scale = getScale();
                    // ограничение масштаба, чтобы при максимальных отдалениях не перекрывались виджеты
                    if (scale < 0.5) {
                        scale = 0.5;
                    }

                    const cx = this.startX + this.width;
                    const cy = this.startY + this.height;

                    const rCanvas = (VOTE_SELECTOR_RADIUS * k) / scale;

                    canv.ctx.beginPath();
                    canv.ctx.arc(cx, cy, rCanvas, 0, 2 * Math.PI);
                    canv.ctx.fillStyle = "rgba(13, 153, 255, 1)";
                    canv.ctx.fill();

                    canv.ctx.strokeStyle = "#fff";
                    canv.ctx.lineWidth = k / scale; // чтобы толщина линии тоже была в пикселях экрана

                    canv.ctx.beginPath();
                    canv.ctx.lineCap = "round";
                    canv.ctx.translate(cx - 1 / scale, cy - 2 / scale); // поправка на пиксели, учитываем масштаб
                    canv.ctx.rotate((45 * Math.PI) / 180);

                    canv.ctx.moveTo((-3 * k) / scale, (5 * k) / scale);
                    canv.ctx.lineTo((2 * k) / scale, (5 * k) / scale);
                    canv.ctx.lineTo((1.5 * k) / scale, (-6.5 * k) / scale);

                    canv.ctx.stroke();
                    canv.ctx.restore();
                }
            }

            if (canv.appInterface.activeVote.isOpen && this.isItemWrapper()) {
                const activeVoteInterface = canv.appInterface.activeVote?.interface;
                const isCandidate = activeVoteInterface?.candidateUIDs?.has(this.children[0].UID);
                if (isCandidate && activeVoteInterface) {
                    activeVoteInterface.drawActiveControl(this.getVoteDrawData());
                }
            }

            if (
                canv.canvasController.showVotesResults &&
                canv.appInterface.voteWindow.opened &&
                canv.appInterface.voteWindow.isShowResult &&
                !canv.appInterface.activeVote.isOpen &&
                this.isItemWrapper()
            ) {
                const isCandidate = canv.appInterface.voteWindow.widgetUIDs.has(
                    this.children[0].UID,
                );
                if (isCandidate) {
                    canv.appInterface.voteWindow.interface.drawCountVotes(this.getVoteDrawData());
                }
            }

            this.children[i].drawOver();
        }
    }

    // is
    isOverGroup() {
        const pt = canv.actionManager.getActionPoint();

        return (
            pt.x >= this.startX &&
            pt.x <= this.startX + this.width &&
            pt.y >= this.startY &&
            pt.y <= this.startY + this.height
        );
    }

    isOverAnyInGroup(isCandidate = false) {
        if (this.children.length == 0) return false;

        if (this.isItemWrapper()) {
            const children = this.children as ElementBase[];

            if (this.angle != 0) {
                return this.isOverGroup();
            }

            return children[0].isOver(isCandidate);
        }

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            if (children[i].isOverAnyInGroup(isCandidate)) {
                return true;
            }
        }

        return false;
    }

    isGroupInArea(area: Area, isFull = false) {
        if (isFull) {
            return (
                area.x <= this.startX &&
                area.y <= this.startY &&
                area.x + area.width >= this.startX + this.width &&
                area.y + area.height >= this.startY + this.height
            );
        }

        // для simulated блока : начало левее и выше правого нижнего угла this фигуры, а конец правее и ниже начала this
        return (
            area.x <= this.startX + this.width &&
            area.y <= this.startY + this.height &&
            area.x + area.width >= this.startX &&
            area.y + area.height >= this.startY
        );
    }

    isAnyItemInArea(area: Area) {
        if (this.isItemWrapper()) {
            const children = this.children as ElementBase[];

            if (this.angle != 0) {
                return this.isGroupInArea(area);
            }

            return children[0].isInArea(area);
        }

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            if (children[i].isAnyItemInArea(area)) {
                return true;
            }
        }

        return false;
    }

    // tools
    collectTreeNodes(collection: TreeNodeWidget[]) {
        if (this.isItemWrapper()) {
            if (this.onlyChild()?.constructorName === "treeNode") {
                collection.push(this.onlyChild() as TreeNodeWidget);
            }
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].collectTreeNodes(collection);
            }
        }
    }

    containsGroup(group: Group) {
        if (this === group) return true;
        if (this.isItemWrapper()) return false;

        return (this.children as Group[]).some((child) => {
            return child.containsGroup(group);
        });
    }

    isItemWrapper() {
        return this.itemWrapper;
    }

    onlyChild<T extends ElementBase>(): T | null {
        if (this.children.length !== 1) return null;

        return this.children[0] as T;
    }

    isGroupOnScreen() {
        const { viewportStartX, viewportStartY, viewportWidth, viewportHeight } = getViewport();

        return (
            viewportStartX <= this.startX + this.width &&
            viewportStartY <= this.startY + this.height &&
            viewportStartX + viewportWidth >= this.startX &&
            viewportStartY + viewportHeight >= this.startY
        );
    }

    isAnyItemOnScreen() {
        if (this.isItemWrapper()) {
            return this.isGroupOnScreen();
        }

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            const result = children[i].isAnyItemOnScreen();

            if (result) {
                return true;
            }
        }

        return false;
    }

    // TODO: нигде не используется
    collectFrames(frames: any[]) {
        if (this.isItemWrapper()) {
            if (this.onlyChild()?.constructorName == "frame") {
                frames.push(this);
            }
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].collectFrames(frames);
            }
        }
    }

    collectNeedInitWidgets(widgets: ElementBase[]) {
        if (this.isItemWrapper()) {
            const child = this.onlyChild();

            if (child?.isNeedInit()) {
                widgets.push(child);
            }
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].collectNeedInitWidgets(widgets);
            }
        }
    }

    hasSubGroup(group: Group) {
        if (this == group) return true;
        if (this.isItemWrapper()) return false;

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            const result = children[i].hasSubGroup(group);

            if (result) {
                return true;
            }
        }

        return false;
    }

    locateWidgetMenu() {
        if (this.isItemWrapper()) {
            return this.onlyChild()?.locateWidgetMenu();
        }
    }

    searchText(text: string, foundItems: any[]) {
        if (this.isItemWrapper()) {
            const result: SearchTextObject[] = this.onlyChild()?.searchText(text) || [];

            for (let i = 0; i < result.length; i++) {
                foundItems.push({
                    group: this,
                    textWidget: result[i].textWidget,
                    mode: result[i].mode,
                });
            }
        } else {
            for (let i = 0; i < this.children.length; i++) {
                this.children[i].searchText(text, foundItems);
            }
        }
    }

    canTranslateOrResize(fromParent = false) {
        const thisChild = this.onlyChild();

        // если элемент является презентацией или слайдом презентации то всегда разрешаем ему resize и translate
        if (
            thisChild instanceof PresentationWidget ||
            (thisChild instanceof frameWidget && thisChild.presentationUID)
        ) {
            return true;
        }

        // проверяем является ли эта группа частью фрейма который является частью презентации
        const isOverlappedOfSelectedFrame = canv.canvasController.selected.some((g) => {
            const child = g.onlyChild();

            if (!child) return false;

            return (
                child instanceof frameWidget && child.presentationUID && child.hasAsOverlapped(this)
            );
        });

        // если нет то мы разрешаем ей resize и translate
        if (!isOverlappedOfSelectedFrame) {
            return true;
        }

        // если да то мы разрешаем только для событий пришедших из родителя фрейма или презентации
        return fromParent;
    }

    // resize translate rotate
    translateGroup(x: number, y: number, isSoft = false, fromParent = false) {
        if (!this.canTranslateOrResize(fromParent)) return;

        if (isSoft && this.isLocked) return;

        if (this.isItemWrapper()) {
            const children = this.children as ElementBase[];

            children[0].translate(x, y);
            this.updateFromPendingTextResizeBounds(children[0]);

            if (!this.anchorGroups) return;

            for (let i = 0; i < this.anchorGroups.length; i++) {
                this.anchorGroups[i].translateGroup(x, y);
            }
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].translateGroup(x, y, isSoft, fromParent);
            }
        }
    }

    resizeGroup(xMove: number, yMove: number, catchPointer: string, fromParent = false) {
        if (!this.canTranslateOrResize(fromParent)) return;

        if (this.isLocked) return;

        if (this.isItemWrapper()) {
            const child = this.onlyChild();
            child?.resize(xMove, yMove, catchPointer);

            if (child instanceof TextWidgetV2) {
                this.updateFromPendingTextResizeBounds(child);
            }

            return;
        }

        const wasProps = canv.canvasController.getSelectedProps([this]);
        const targetProps = canv.canvasController.getSelectedProps([this]);

        targetProps.width += xMove;
        targetProps.height += yMove;

        const gData: RatioData[] = [];
        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            const currentGroup = children[i];

            gData.push({
                widthRatio: wasProps.width != 0 ? currentGroup.width / wasProps.width : 0.01,
                heightRatio: wasProps.height != 0 ? currentGroup.height / wasProps.height : 0.01,
                xRatio:
                    wasProps.width != 0
                        ? (currentGroup.startX + currentGroup.width / 2 - wasProps.startX) /
                          wasProps.width
                        : 0.01,
                yRatio:
                    wasProps.height != 0
                        ? (currentGroup.startY + currentGroup.height / 2 - wasProps.startY) /
                          wasProps.height
                        : 0.01,
            });
        }

        for (let i = 0; i < children.length; i++) {
            const currentGroup = children[i];

            currentGroup.resizeGroup(
                targetProps.width * gData[i].widthRatio - currentGroup.width,
                targetProps.height * gData[i].heightRatio - currentGroup.height,
                this.catchPointer,
            );
            currentGroup.translateGroup(
                targetProps.startX +
                    gData[i].xRatio * targetProps.width -
                    (currentGroup.startX + currentGroup.width / 2),
                targetProps.startY +
                    gData[i].yRatio * targetProps.height -
                    (currentGroup.startY + currentGroup.height / 2),
            );
        }
    }

    translateBeforeRotate(deltaAngle: number, Cx: number, Cy: number) {
        const distanceX = this.startX + this.width / 2 - Cx;
        const distanceY = Cy - (this.startY + this.height / 2);
        const r = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        if (r !== 0) {
            const cosB = distanceX / r;
            let Bangle = Math.acos(cosB) * (180 / Math.PI);

            if (distanceY < 0) {
                Bangle *= -1;
            }

            const B1Angle = Bangle + deltaAngle;

            const distanceX2 = r * Math.cos(B1Angle * (Math.PI / 180));
            const distanceY2 = r * Math.sin(B1Angle * (Math.PI / 180));

            const moveX = distanceX2 - distanceX;
            const moveY = distanceY - distanceY2;

            this.translateGroup(moveX, moveY);
        }
    }

    rotateGroup(deltaAngle: number, Cx: number, Cy: number) {
        if (this.isLocked) return;

        if (this.isItemWrapper()) {
            if (
                this.onlyChild() instanceof frameWidget ||
                this.onlyChild() instanceof TableWidget
            ) {
                this.translateBeforeRotate(deltaAngle, Cx, Cy);
            } else if (
                this.onlyChild() instanceof TextWidgetV2 &&
                (this.onlyChild() as TextWidgetV2).getIsEditing()
            ) {
                this.translateBeforeRotate(deltaAngle, Cx, Cy);
                this.angle += deltaAngle;
                canv.textEditingController.updateOverlayTransform();
            } else {
                if (!this.onlyChild()?.rotatable) return;

                this.translateBeforeRotate(deltaAngle, Cx, Cy);
                this.angle += deltaAngle;
            }

            this.bottomTopUpdateDims();
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].rotateGroup(deltaAngle, Cx, Cy);
            }
        }
    }

    // BecomingSelected Reset beforeDeletion
    groupTranslatingStart() {
        if (this.isItemWrapper()) {
            this.onlyChild()?.translatingStart();
            return;
        }

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            children[i].groupTranslatingStart();
        }
    }

    groupTranslatingStop() {
        if (this.isItemWrapper()) {
            this.onlyChild()?.translatingStop();
            return;
        }

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            children[i].groupTranslatingStop();
        }
    }

    groupRotatingStart() {
        if (this.isItemWrapper()) {
            this.onlyChild()?.rotatingStart();
            return;
        }

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            children[i].groupRotatingStart();
        }
    }

    groupRotatingStop() {
        if (this.isItemWrapper()) {
            this.onlyChild()?.rotatingStop();
            return;
        }

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            children[i].groupRotatingStop();
        }
    }

    groupResizingStart = () => {
        if (this.isItemWrapper()) {
            this.onlyChild()?.resizingStart();
            return;
        }

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            children[i].groupResizingStart();
        }
    };

    groupResizingStop(catchPointer: string) {
        if (this.isItemWrapper()) {
            this.onlyChild()?.resizingStop(catchPointer);
            return;
        }

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            children[i].groupResizingStop(catchPointer);
        }
    }

    touchAllArrows() {
        for (let i = 0; i < this.arrows.length; i++) {
            canv.eventManager.createTouchEvent(this.arrows[i].arrowItemUID);
        }
    }

    beforeDeletion() {
        // генерим touch events с UID стрелок
        // не генерим property для позиций стрелок, потому что при buildEvent все стрелки проставятся через updateDims группы
        this.touchAllArrows();

        if (this.isItemWrapper()) {
            this.onlyChild()?.beforeDeletion();

            canv.canvasController.hashGrid?.remove(this);
        } else {
            for (let i = 0; i < this.children.length; i++) {
                this.children[i].beforeDeletion();
            }
        }
    }

    // TryAction
    TryAction(type: string) {
        if (!this.isItemWrapper()) return;

        this.children[0].TryAction(type);
    }

    // FindByUID TrailUID
    FindByUID(UID: string): Group | ElementBase | null {
        if (this.UID === UID) {
            return this;
        }

        let foundItem: Group | ElementBase | null = null;

        if (this.isItemWrapper()) {
            if (this.onlyChild()?.UID === UID) {
                foundItem = this.onlyChild();
                return foundItem;
            }
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; ++i) {
                foundItem = children[i].FindByUID(UID);

                if (foundItem) {
                    return foundItem;
                }
            }
        }

        return foundItem;
    }

    FindUIDByTrailUID(trailUID: string) {
        let foundUID = "";

        if (this.isItemWrapper()) {
            const child = this.onlyChild();

            if (child?.trailUID == trailUID) {
                foundUID = child.UID;

                return foundUID;
            }
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; ++i) {
                foundUID = children[i].FindUIDByTrailUID(trailUID);

                if (foundUID !== "") {
                    return foundUID;
                }
            }
        }

        return foundUID;
    }

    clearTrailUIDs() {
        this.trailUID = "";

        if (this.isItemWrapper()) {
            const child = this.onlyChild();

            if (child) child.trailUID = "";
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; ++i) {
                children[i].clearTrailUIDs();
            }
        }
    }

    // ARROWS
    // вызывается из arrow beforeDeletion
    TryRemoveArrowRecursively(arrow: ArrowWidget, point: string) {
        this.TryRemoveArrow(arrow, point);

        for (let i = 0; i < this.children.length && !this.isItemWrapper(); i++) {
            const children = this.children as Group[];

            children[i].TryRemoveArrowRecursively(arrow, point);
        }
    }

    // вызывается из arrow translate
    hasArrow(arrowUID: string, point: string) {
        for (let i = 0; i < this.arrows.length; i++) {
            if (this.arrows[i].arrowItemUID == arrowUID && this.arrows[i].point == point) {
                return this;
            }
        }

        if (!this.isItemWrapper()) {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                const result = children[i].hasArrow(arrowUID, point);

                if (result != null) {
                    return result;
                }
            }
        }

        return null;
    }

    getArrowCopy(arrow: ArrowData) {
        return {
            point: arrow.point,
            arrowItemUID: arrow.arrowItemUID,
            place: arrow.place,
            share: arrow.share,
        };
    }

    TryRemoveArrow(arrow: ArrowWidget | null, point: string) {
        if (arrow == null) return;

        for (let i = 0; i < this.arrows.length; i++) {
            if (this.arrows[i].arrowItemUID == arrow.UID && this.arrows[i].point == point) {
                canv.eventManager.createPropertyBuildEvent(
                    this.UID,
                    "arrows",
                    "remove",
                    this.getArrowCopy(this.arrows[i]),
                );
                this.arrows.splice(i, 1);
                return;
            }
        }
    }

    TryAddArrow(arrow: ArrowWidget, place: ArrowPlace, point: string, share: number) {
        const newArrow = { point, arrowItemUID: arrow.UID, place, share };

        for (let i = 0; i < this.arrows.length; i++) {
            if (this.arrows[i].arrowItemUID == arrow.UID && this.arrows[i].point == point) {
                return;
            }
        }

        const foundItem = canv.canvasController.FindByUID(arrow.UID);

        if (foundItem == null) {
            return;
        }

        this.arrows.push(newArrow);
        canv.eventManager.createPropertyBuildEvent(this.UID, "arrows", "add", newArrow);
    }

    isAroundConnectors() {
        if (this.isItemWrapper()) {
            const children = this.children as ElementBase[];

            if (children[0].disableConnectors) {
                return {
                    status: false,
                };
            }
        }

        const pt = canv.actionManager.getActionPoint();

        // Обязательно проставлять this.pointerSize
        this.pointerSize = canv.actionManager.getZoomConstant(10);

        const allowedDiff = canv.actionManager.getZoomConstant(10);

        // Для понимания логики выражений нужно перенести allowedDiff в правую часть

        if (
            pt.x + allowedDiff > this.startX + this.width / 2 - this.pointerSize / 2 &&
            pt.x - allowedDiff < this.startX + this.width / 2 + this.pointerSize / 2 &&
            pt.y + allowedDiff > this.startY - this.pointerSize / 2 &&
            pt.y - allowedDiff < this.startY + this.pointerSize / 2
        ) {
            return {
                status: true,
                x: this.startX + this.width / 2,
                y: this.startY,
                place: "top",
                share: 0.5,
                group: this,
            };
        }

        if (
            pt.x + allowedDiff > this.startX + this.width - this.pointerSize / 2 &&
            pt.x - allowedDiff < this.startX + this.width + this.pointerSize / 2 &&
            pt.y + allowedDiff > this.startY + this.height / 2 - this.pointerSize / 2 &&
            pt.y - allowedDiff < this.startY + this.height / 2 + this.pointerSize / 2
        ) {
            return {
                status: true,
                x: this.startX + this.width,
                y: this.startY + this.height / 2,
                place: "right",
                share: 0.5,
                group: this,
            };
        }

        if (
            pt.x + allowedDiff > this.startX + this.width / 2 - this.pointerSize / 2 &&
            pt.x - allowedDiff < this.startX + this.width / 2 + this.pointerSize / 2 &&
            pt.y + allowedDiff > this.startY + this.height - this.pointerSize / 2 &&
            pt.y - allowedDiff < this.startY + this.height + this.pointerSize / 2
        ) {
            return {
                status: true,
                x: this.startX + this.width / 2,
                y: this.startY + this.height,
                place: "bottom",
                share: 0.5,
                group: this,
            };
        }

        if (
            pt.x + allowedDiff > this.startX - this.pointerSize / 2 &&
            pt.x - allowedDiff < this.startX + this.pointerSize / 2 &&
            pt.y + allowedDiff > this.startY + this.height / 2 - this.pointerSize / 2 &&
            pt.y - allowedDiff < this.startY + this.height / 2 + this.pointerSize / 2
        ) {
            return {
                status: true,
                x: this.startX,
                y: this.startY + this.height / 2,
                place: "left",
                share: 0.5,
                group: this,
            };
        }

        return { status: false };
    }

    tryMagnetPosition() {
        if (this.isItemWrapper()) {
            const children = this.children as ElementBase[];

            if (children[0].disableConnectors) {
                return { status: false };
            }
        }

        const pt = canv.actionManager.getActionPoint();
        const ifConnectorManget = this.isAroundConnectors();

        if (ifConnectorManget.status) {
            return ifConnectorManget;
        }

        const gap = canv.actionManager.getZoomConstant(10);

        // top
        if (
            pt.x >= this.startX &&
            pt.x <= this.startX + this.width &&
            pt.y >= this.startY - gap &&
            pt.y <= this.startY + gap
        ) {
            return {
                status: true,
                x: pt.x,
                y: this.startY,
                place: "top",
                share: (pt.x - this.startX) / this.width,
                group: this,
            };
        }

        // right
        if (
            pt.x >= this.startX + this.width - gap &&
            pt.x <= this.startX + this.width + gap &&
            pt.y >= this.startY &&
            pt.y <= this.startY + this.height
        ) {
            return {
                status: true,
                x: this.startX + this.width,
                y: pt.y,
                place: "right",
                share: (pt.y - this.startY) / this.height,
                group: this,
            };
        }

        // bottom
        if (
            pt.x >= this.startX &&
            pt.x <= this.startX + this.width &&
            pt.y >= this.startY + this.height - gap &&
            pt.y <= this.startY + this.height + gap
        ) {
            return {
                status: true,
                x: pt.x,
                y: this.startY + this.height,
                place: "bottom",
                share: (pt.x - this.startX) / this.width,
                group: this,
            };
        }

        // left
        if (
            pt.x >= this.startX - gap &&
            pt.x <= this.startX + gap &&
            pt.y >= this.startY &&
            pt.y <= this.startY + this.height
        ) {
            return {
                status: true,
                x: this.startX,
                y: pt.y,
                place: "left",
                share: (pt.y - this.startY) / this.height,
                group: this,
            };
        }

        return { status: false };
    }

    TryMagnet(arrow: ArrowWidget | null = null, point = "", mag = false) {
        if (!this.isGroupOnScreen()) {
            return { status: false };
        }

        //@ts-ignore TODO: убереться, когда перепишем arrowWidget на TS
        if (arrow != null && this.hasSubGroup(arrow.parent)) {
            return { status: false };
        }

        const res = this.tryMagnetPosition();

        if (res.status) {
            if (mag) {
                this.TryRemoveArrow(arrow, point);
                //@ts-expect-error TODO: убереться, когда перепишем arrowWidget на TS
                this.TryAddArrow(arrow, res.place, point, res.share);
                this.TurnOffMagnetViewRecursively();
            } else {
                this.TurnOnMagnetView();
            }

            return res;
        }
        this.TurnOffMagnetViewRecursively();

        for (let i = 0; i < this.children.length && !this.isItemWrapper(); i++) {
            const children = this.children as Group[];

            const localRes = children[i].TryMagnet(arrow, point, mag);

            if (localRes.status) {
                return localRes;
            }
        }

        return { status: false };
    }

    TurnOnMagnetView() {
        this.TurnOffMagnetViewRecursively();

        this.needLowOutline = true;
        this.needConnectors = true;
    }

    TurnOffMagnetViewRecursively() {
        this.needLowOutline = false;
        this.needConnectors = false;

        if (this.isItemWrapper()) return;

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            children[i].TurnOffMagnetViewRecursively();
        }
    }

    calcConnectorPosition(connector: string, share: number) {
        if (!isBrowser()) return;

        if (connector === "top") {
            return {
                x: this.startX + share * this.width,
                y: this.startY,
            };
        }
        if (connector === "right") {
            return {
                x: this.startX + this.width,
                y: this.startY + share * this.height,
            };
        }
        if (connector === "bottom") {
            return {
                x: this.startX + share * this.width,
                y: this.startY + this.height,
            };
        }
        if (connector === "left") {
            return {
                x: this.startX,
                y: this.startY + share * this.height,
            };
        }
    }

    // recursiveResyncArrows вызывает resyncArrow
    resyncArrows() {
        const arrowsList = this.getArrows();

        this.arrows = [];

        for (let i = 0; i < arrowsList.length; i++) {
            const arrowData: ArrowData = arrowsList[i];

            arrowData.arrowItemUID = canv.canvasController.FindUIDByTrailUID(
                arrowData.arrowItemUID,
            );

            const realUID = canv.canvasController.FindByUID(arrowData.arrowItemUID);

            if (realUID == null) continue;

            this.arrows.push(arrowData);
        }

        this.updateDims();
    }

    recursiveResyncArrows() {
        this.resyncArrows();

        if (this.isItemWrapper()) return;

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            children[i].recursiveResyncArrows();
        }
    }

    // Далее для widgetMenu
    collectWidgetOptions(options: any[]) {
        if (this.isItemWrapper()) {
            const firstChild = this.children?.[0] as ElementBase;
            if (this.isLocked && !firstChild?.allowOptionsForLocked) {
                return;
            }
            options.push(this.onlyChild()?.getWidgetOptions(this.isLocked));
            return;
        }

        const children = this.children as Group[];

        for (let i = 0; i < children.length; i++) {
            children[i].collectWidgetOptions(options);
        }
    }

    // Далее для levelLines
    getVisibleItemWrappersExceptSelected(groups: Group[]) {
        if (!this.isAnyItemOnScreen()) return;
        if (canv.canvasController.selected.indexOf(this) !== -1) return;

        if (this.isItemWrapper()) {
            groups.push(this);
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].getVisibleItemWrappersExceptSelected(groups);
            }
        }
    }

    TryLevelLines(selectedRect: Rect, result: any, allowed: string[]) {
        if (!this.isItemWrapper()) return;
        if (this.onlyChild()?.disableLevelLines) {
            return result;
        }

        const gap = 5 / canv.m[0];

        // Выставляем один из X
        if (selectedRect.startX >= this.startX - gap && selectedRect.startX <= this.startX + gap) {
            // Левый край с левый краем
            const res = this.startX;

            if ((allowed.indexOf("left") != -1 && result.x == null) || result.x == res) {
                result.x = res;
                result.vLines.push({
                    code: "left",
                    UID: this.UID,
                });
            }
        }

        if (
            selectedRect.startX >= this.startX + this.width - gap &&
            selectedRect.startX <= this.startX + this.width + gap
        ) {
            // Левый край с правым краем
            const res = this.startX + this.width;

            if ((allowed.indexOf("left") != -1 && result.x == null) || result.x == res) {
                result.x = res;
                result.vLines.push({
                    code: "left",
                    UID: this.UID,
                });
            }
        }

        if (
            selectedRect.startX + selectedRect.width / 2 >= this.startX + this.width / 2 - gap &&
            selectedRect.startX + selectedRect.width / 2 <= this.startX + this.width / 2 + gap
        ) {
            // Центры
            const res = this.startX + this.width / 2 - selectedRect.width / 2;

            if ((allowed.indexOf("hCenter") != -1 && result.x == null) || result.x == res) {
                result.x = res;
                result.vLines.push({
                    code: "hCenter",
                    UID: this.UID,
                });
            }
        }

        if (
            selectedRect.startX + selectedRect.width >= this.startX - gap &&
            selectedRect.startX + selectedRect.width <= this.startX + gap
        ) {
            // Правый край с левым краем
            const res = this.startX - selectedRect.width;

            if ((allowed.indexOf("right") != -1 && result.x == null) || result.x == res) {
                result.x = res;
                result.vLines.push({
                    code: "right",
                    UID: this.UID,
                });
            }
        } else if (
            selectedRect.startX + selectedRect.width >= this.startX + this.width - gap &&
            selectedRect.startX + selectedRect.width <= this.startX + this.width + gap
        ) {
            // Правый край с правым краем
            const res = this.startX + this.width - selectedRect.width;

            if ((allowed.indexOf("right") != -1 && result.x == null) || result.x == res) {
                result.x = res;
                result.vLines.push({
                    code: "right",
                    UID: this.UID,
                });
            }
        }

        // Выставляем один из Y
        if (selectedRect.startY >= this.startY - gap && selectedRect.startY <= this.startY + gap) {
            // Верхний край с верхний краем
            const res = this.startY;

            if ((allowed.indexOf("top") != -1 && result.y == null) || result.y == res) {
                result.y = res;
                result.hLines.push({
                    code: "top",
                    UID: this.UID,
                });
            }
        }

        if (
            selectedRect.startY >= this.startY + this.height - gap &&
            selectedRect.startY <= this.startY + this.height + gap
        ) {
            // Верхний край с нижним краем
            const res = this.startY + this.height;

            if ((allowed.indexOf("top") != -1 && result.y == null) || result.y == res) {
                result.y = res;
                result.hLines.push({
                    code: "top",
                    UID: this.UID,
                });
            }
        }

        if (
            selectedRect.startY + selectedRect.height / 2 >= this.startY + this.height / 2 - gap &&
            selectedRect.startY + selectedRect.height / 2 <= this.startY + this.height / 2 + gap
        ) {
            // Центры
            const res = this.startY + this.height / 2 - selectedRect.height / 2;

            if ((allowed.indexOf("vCenter") != -1 && result.y == null) || result.y == res) {
                result.y = res;
                result.hLines.push({
                    code: "vCenter",
                    UID: this.UID,
                });
            }
        }

        if (
            selectedRect.startY + selectedRect.height >= this.startY - gap &&
            selectedRect.startY + selectedRect.height <= this.startY + gap
        ) {
            // Нижний край с верхний краем
            const res = this.startY - selectedRect.height;

            if ((allowed.indexOf("bottom") != -1 && result.y == null) || result.y == res) {
                result.y = res;
                result.hLines.push({
                    code: "bottom",
                    UID: this.UID,
                });
            }
        }

        if (
            selectedRect.startY + selectedRect.height >= this.startY + this.height - gap &&
            selectedRect.startY + selectedRect.height <= this.startY + this.height + gap
        ) {
            // Нижний край с нижним краем
            const res = this.startY + this.height - selectedRect.height;

            if ((allowed.indexOf("bottom") != -1 && result.y == null) || result.y == res) {
                result.y = res;
                result.hLines.push({
                    code: "bottom",
                    UID: this.UID,
                });
            }
        }

        return result;
    }

    // TODO Закомментировали эти 2 метода что бы посмотреть производительность больших досок без красных линий

    // TryDistancesX(selectedRect: Rect, result: any, allowed: string, distancesX: any[]) {
    //     if (!this.isItemWrapper()) return;

    //     if (this.onlyChild()?.disableLevelLines) {
    //         return result;
    //     }

    //     const gap = 5 / canv.m[0];
    //     const staticFault = 0;
    //     const vCrossing = this.startY <= selectedRect.startY + selectedRect.height && this.startY + this.height >= selectedRect.startY;

    //     for (let i = 0; i < distancesX.length && vCrossing; i++) {
    //         const distInfo = distancesX[i];

    //         if (selectedRect.startX >= this.startX + this.width + distInfo.distance - gap && selectedRect.startX <= this.startX + this.width + distInfo.distance + gap) {
    //             const res = this.startX + this.width + distInfo.distance;

    //             if (allowed.indexOf("left") != -1 && result.x == null || result.x == res) {
    //                 result.x = res;

    //                 if (result.hDist.length == 0) {
    //                     for (let k = 0; k < distancesX.length; k++) {
    //                         if (distancesX[k].distance >= distInfo.distance - staticFault && distancesX[k].distance <= distInfo.distance + staticFault) {
    //                             result.hDist.push(distancesX[k]);
    //                         }
    //                     }
    //                 }

    //                 if (distInfo.distance >= result.hDist[0].distance - staticFault && distInfo.distance <= result.hDist[0].distance + staticFault) {
    //                     result.hDist.push({
    //                         distance: distInfo.distance,
    //                         startX: this.startX + this.width,
    //                         endX: this.startX + this.width + distInfo.distance,
    //                         startUID: this.UID,
    //                     });
    //                 }

    //             }
    //         }

    //         if (selectedRect.startX + selectedRect.width >= this.startX - distInfo.distance - gap && selectedRect.startX + selectedRect.width <= this.startX - distInfo.distance + gap) {
    //             const res = this.startX - canv.canvasController.width - distInfo.distance;

    //             if (allowed.indexOf("right") != -1 && result.x == null || result.x == res) {
    //                 result.x = res;

    //                 if (result.hDist.length == 0) {
    //                     for (let k = 0; k < distancesX.length; k++) {
    //                         if (distancesX[k].distance >= distInfo.distance - staticFault && distancesX[k].distance <= distInfo.distance + staticFault) {
    //                             result.hDist.push(distancesX[k]);
    //                         }
    //                     }
    //                 }

    //                 if (distInfo.distance >= result.hDist[0].distance - staticFault && distInfo.distance <= result.hDist[0].distance + staticFault) {
    //                     result.hDist.push({
    //                         distance: distInfo.distance,
    //                         startX: this.startX,
    //                         endX: this.startX - distInfo.distance,
    //                         startUID: this.UID,
    //                     });
    //                 }

    //             }
    //         }
    //     }
    // }

    // TryDistancesY(selectedRect: Rect, result: any, allowed: string, distancesY: any[]) {
    //     if (!this.isItemWrapper()) return;
    //     if (this.onlyChild()?.disableLevelLines) {
    //         return result;
    //     }

    //     const gap = 5 / canv.m[0];
    //     const staticFault = 0;
    //     const hCrossing = this.startX <= selectedRect.startX + selectedRect.width && this.startX + this.width >= selectedRect.startX;

    //     for (let i = 0; i < distancesY.length && hCrossing; i++) {
    //         const distInfo = distancesY[i];

    //         if (selectedRect.startY >= this.startY + this.height + distInfo.distance - gap && selectedRect.startY <= this.startY + this.height + distInfo.distance + gap) {
    //             const res = Math.floor((this.startY + this.height + distInfo.distance) * 1) / 1;

    //             if (allowed.indexOf("top") != -1 && result.y == null || result.y == res) {
    //                 result.y = res;

    //                 if (result.vDist.length == 0) {
    //                     for (let k = 0; k < distancesY.length; k++) {
    //                         if (distancesY[k].distance >= distInfo.distance - staticFault && distancesY[k].distance <= distInfo.distance + staticFault) {
    //                             result.vDist.push(distancesY[k]);
    //                         }
    //                     }
    //                 }

    //                 if (distInfo.distance >= result.vDist[0].distance - staticFault && distInfo.distance <= result.vDist[0].distance + staticFault) {
    //                     result.vDist.push({
    //                         distance: distInfo.distance,
    //                         startY: this.startY + this.height,
    //                         endY: this.startY + this.height + distInfo.distance,
    //                         startUID: this.UID,
    //                     });
    //                 }
    //             }
    //         }

    //         if (selectedRect.startY + selectedRect.height >= this.startY - distInfo.distance - gap && selectedRect.startY + selectedRect.height <= this.startY - distInfo.distance + gap) {
    //             const res = Math.floor((this.startY - canv.canvasController.height - distInfo.distance) * 1) / 1;

    //             if (allowed.indexOf("bottom") != -1 && result.y == null || result.y == res) {
    //                 result.y = res;

    //                 if (result.vDist.length == 0) {
    //                     for (let k = 0; k < distancesY.length; k++) {
    //                         if (distancesY[k].distance >= distInfo.distance - staticFault && distancesY[k].distance <= distInfo.distance + staticFault) {
    //                             result.vDist.push(distancesY[k]);
    //                         }
    //                     }
    //                 }

    //                 if (distInfo.distance >= result.vDist[0].distance - staticFault && distInfo.distance <= result.vDist[0].distance + staticFault) {
    //                     result.vDist.push({
    //                         distance: distInfo.distance,
    //                         startY: selectedRect.startY + selectedRect.height,
    //                         endY: this.startY,
    //                         startUID: this.UID,
    //                     });
    //                 }
    //             }
    //         }
    //     }
    // }

    // Dumps
    findAllWidgetsByConstructorName(constructorName: WidgetConstructorName) {
        const result: ElementBase[] = [];

        if (this.isItemWrapper()) {
            const onlyChild = this.onlyChild();

            if (onlyChild && onlyChild.constructorName === constructorName) {
                result.push(onlyChild);
            }
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                result.push(...children[i].findAllWidgetsByConstructorName(constructorName));
            }
        }

        return result;
    }

    getArrows() {
        return this.arrows.map((arrow) => {
            return {
                point: arrow.point,
                arrowItemUID: arrow.arrowItemUID,
                place: arrow.place,
                share: getNumberProperty("share", arrow, 0.5),
            };
        });
    }

    loadArrows(arrows: ArrowData[]) {
        this.arrows = [];

        if (arrows == undefined) return;

        for (let i = 0; i < arrows.length; i++) {
            this.arrows.push({
                point: getStringProperty("point", arrows[i]),
                arrowItemUID: getStringProperty("arrowItemUID", arrows[i]),
                place: getStringProperty("place", arrows[i]),
                share: getNumberProperty("share", arrows[i], 0.5),
            });
        }
    }

    loadGroup(dump: Group) {
        this.UID = dump.UID;
        this.indexByParent = dump.indexByParent;
        this.itemWrapper = dump.itemWrapper;
        this.loadArrows(dump.arrows);
        this.angle = dump.angle;
        this.parentUID = dump.parentUID;

        for (let i = 0; i < dump.children.length; i++) {
            const children = dump.children as Group[];

            if (children[i].type === "group") {
                const newG = new Group();

                newG.parent = this;
                this.children.push(newG);
                newG.loadGroup(children[i]);
            } else {
                const b = children[i].type;
                const newItem = this.getWidgetByName(b) as ElementBase;

                if (newItem) {
                    this.addChild(newItem);
                    //@ts-expect-error
                    newItem.loadState(children[i].state, "full");
                    newItem.parentUID = this.UID;
                }
            }
        }

        const compare = (a: { indexByParent: string }, b: { indexByParent: string }) => {
            if (a.indexByParent < b.indexByParent) {
                return -1;
            }
            if (a.indexByParent > b.indexByParent) {
                return 1;
            }
            return 0;
        };

        if (!this.isItemWrapper()) {
            //@ts-expect-error TODO: уберем после того как перепишем ElementBase на TS
            this.children.sort(compare);
        }
    }

    getWidgetByName(constructorName: string) {
        if (constructorName === "arrow") {
            return new ArrowWidget(0, 0, 0, 0);
        }
        if (constructorName === "frame") {
            return new frameWidget(0, 0);
        }
        if (constructorName === "image") {
            return new ImageWidget();
        }
        if (constructorName === "gif") {
            //@ts-ignore
            return new GifWidget();
        }
        if (constructorName === "shape") {
            return new ShapeWidget();
        }
        if (constructorName === "sketch") {
            return new SketchWidget();
        }
        if (constructorName === "sticker") {
            return new StickerWidget(0, 0, "");
        }
        if (constructorName === "treeNode") {
            return new TreeNodeWidget();
        }
        if (constructorName === "comment") {
            //@ts-ignore
            return new CommentWidget();
        }
        if (constructorName === "documentViewer") {
            return new DocumentViewerWidget();
        }
        if (constructorName === "emoji") {
            return new EmojiWidget();
        }
        if (constructorName === "presentation") {
            return new PresentationWidget(0, 0);
        }
    }

    // fixWidgets saveWidgets
    fixWidgets(reason: string, withAnchorGroups = true) {
        for (let i = 0; i < this.arrows.length; i++) {
            const arrow = canv.canvasController.FindByUID(this.arrows[i].arrowItemUID);

            if (!arrow || !(arrow instanceof ArrowWidget) || !arrow.parent) continue;

            arrow.parent.fixWidgets("move", false);
        }

        if (this.isItemWrapper()) {
            const child = this.onlyChild();
            if (!child) return;

            child.fixState(reason);

            if (
                withAnchorGroups &&
                reason === "move" &&
                !CONSTRUCTORS_WITHOUT_ANCHORS.includes(child.constructorName)
            ) {
                const anchorGroups = queryAreaByGroup(this, false, true);
                if (anchorGroups.length) {
                    this.anchorGroups = anchorGroups;
                    for (let i = 0; i < this.anchorGroups.length; i++) {
                        this.anchorGroups[i].fixWidgets(reason, false);
                    }
                }
            }
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].fixWidgets(reason, withAnchorGroups);
            }
        }
    }

    saveWidgets(reason: string, withAnchorGroups = true) {
        if (this.isItemWrapper()) {
            const child = this.onlyChild();
            if (!child) return;

            child.saveState(reason);

            if (
                withAnchorGroups &&
                reason === "move" &&
                !CONSTRUCTORS_WITHOUT_ANCHORS.includes(child.constructorName)
            ) {
                if (this.anchorGroups) {
                    for (let i = 0; i < this.anchorGroups.length; i++) {
                        this.anchorGroups[i].saveWidgets(reason, false);
                    }
                    this.anchorGroups = null;
                }
            }
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].saveWidgets(reason, withAnchorGroups);
            }
        }

        for (let i = 0; i < this.arrows.length; i++) {
            const arrow = canv.canvasController.FindByUID(this.arrows[i].arrowItemUID);

            if (!arrow || !(arrow instanceof ArrowWidget) || !arrow.parent) {
                continue;
            }

            arrow.parent.saveWidgets("move", false);
        }
    }

    isInteraction(context: string, force = false) {
        if (this.isItemWrapper()) {
            return this.onlyChild()?.isInteraction(context, force);
        }
        if (canv.canvasController.selected.length === 1) {
            const group = this.children.find((child) => {
                return child.UID === canv.canvasController.selected[0].UID;
            });
            if (group instanceof Group && group?.isItemWrapper()) {
                return group.onlyChild()?.isInteraction(context, force);
            }
        }
        return { type: "none" };
    }

    stopInteraction() {
        if (this.isItemWrapper()) {
            this.onlyChild()?.stopInteraction();
        }
    }

    shouldStopInteractionOnModeChange(nextMode: string) {
        if (this.isItemWrapper()) {
            return this.onlyChild()?.shouldStopInteractionOnModeChange?.(nextMode) ?? true;
        }

        return true;
    }

    setParent(group: Group | null = null, disableRemoving = false) {
        // disableRemoving = true только в create branch. Чтобы при провязки копии существующей branch не отвязывались enitity существующей ветки
        if (!disableRemoving) {
            if (this.parentUID) {
                const currentParent = canv.canvasController.FindByUID(this.parentUID);

                if (currentParent) {
                    // такое может быть из build event. Надо убрать все это перейдя на плоский список
                    for (let i = 0; i < (currentParent as Group).children?.length; i++) {
                        if ((currentParent as Group).children[i].UID === this.UID) {
                            (currentParent as Group).children.splice(i, 1);
                            (currentParent as Group).updateDims();
                            break;
                        }
                    }
                }
            }
        }

        if (group != null) {
            this.parentUID = group.UID;
            this.parent = group;

            if (!group.children.some((child) => child.UID === this.UID)) {
                group.children.push(this as unknown as Group & ElementBase);

                sortByIndexByParent(group.children as Group[]);
            }
        } else {
            this.parentUID = "";
            this.parent = null;
        }

        this.bottomTopUpdateDims();
    }

    addToCollection() {
        if (this.isItemWrapper()) {
            canv.canvasController.items.push(this);
            sortByIndexByParent(canv.canvasController.items);
        } else {
            canv.canvasController.groups.set(this.UID, this);
        }

        this.bottomTopUpdateDims();
    }

    checkDeletion(UIDs: Map<any, any>) {
        for (let i = 0; i < this.children.length; i++) {
            if (!UIDs.has(this.children[i].UID)) {
                return false;
            }
        }

        return true;
    }

    collectEntitiesStates(states: any[]) {
        states.push(this.getState("full"));

        if (this.isItemWrapper()) {
            states.push(this.onlyChild()?.getState("full"));
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].collectEntitiesStates(states);
            }
        }
    }

    regenerateUIDS(parentUID: string, setTrails = false) {
        this.parentUID = parentUID;
        this.trailUID = this.UID;
        this.UID = generateUID();

        if (this.isItemWrapper()) {
            const child = this.onlyChild();

            if (!child) return;

            if (setTrails) {
                child.trailUID = child.UID;
            }

            child.parentUID = this.UID;
            child.UID = generateUID();
            child.regenerateUIDS();
        } else {
            (this.children as Group[]).forEach((child) => {
                child.regenerateUIDS(this.UID, setTrails);
            });
        }
    }

    onIndexChange() {
        if (this.parent == null) {
            sortByIndexByParent(canv.canvasController.items);
        } else {
            sortByIndexByParent(this.parent.children as Group[]);
        }
    }

    buildPropertyArrowsHelper(property: ArrowData, action: string) {
        const itemIndex = this.arrows.findLastIndex((arrow: ArrowData) => {
            return arrow.point == property.point && arrow.arrowItemUID == property.arrowItemUID;
        });

        if (itemIndex != -1) {
            this.arrows.splice(itemIndex, 1);
        }

        if (action == "add") {
            const tmpArrow = {
                point: property.point,
                arrowItemUID: property.arrowItemUID,
                place: property.place,
                share: getNumberProperty("share", property, 0.5),
            };
            this.arrows.push(tmpArrow);

            // нужно чтобы при undo(привязка стрелки) и уже перемещенной группе, стрелка подвинулась в нужную точку
            this.updateArrowPosition(tmpArrow);
        }
    }

    buildProperty(property: ArrowData, fixType: string, action: string) {
        switch (fixType) {
            case "arrows":
                this.buildPropertyArrowsHelper(property, action);
        }
    }

    checkEvent() {
        return true;
    }

    getFixType(reason: string) {
        let fixType = "full";

        switch (reason) {
            case "rotate":
                fixType = "rotate";
                break;
            case "indexByParent":
                fixType = "indexByParent";
                break;
            case "parentUID":
                fixType = "parentUID";
                break;
            case "lock":
                fixType = "lock";
                break;
        }

        return fixType;
    }

    getState<T extends string>(fixType: T): GroupStateByFixState<T> {
        switch (fixType) {
            case "rotate":
                return {
                    angle: this.angle,
                } as GroupStateByFixState<T>;
            case "indexByParent":
                return {
                    indexByParent: this.indexByParent,
                } as GroupStateByFixState<T>;
            case "parentUID":
                return {
                    parentUID: this.parentUID,
                } as GroupStateByFixState<T>;
            case "lock":
                return {
                    isLocked: this.isLocked,
                } as GroupStateByFixState<T>;
            case "full":
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    itemWrapper: this.itemWrapper,
                    entityType: "group",
                    indexByParent: this.indexByParent,
                    arrows: this.getArrows(),
                    angle: this.angle,
                    isLocked: this.isLocked,
                } as GroupStateByFixState<T>;
            default: {
                return {} as GroupStateByFixState<T>;
            }
        }
    }

    loadState(state: Group, fixType: string) {
        switch (fixType) {
            case "rotate":
                this.angle = getNumberProperty("angle", state);
                break;
            case "indexByParent":
                this.indexByParent = getStringProperty("indexByParent", state);
                this.onIndexChange();
                break;
            case "parentUID":
                this.setParent(
                    canv.canvasController.FindByUID(getStringProperty("parentUID", state)) as Group,
                ); // null это ок
                break;
            case "lock":
                this.isLocked = getBoolProperty("isLocked", state);
                break;
            case "full":
                this.UID = getStringProperty("UID", state);
                this.parentUID = getStringProperty("parentUID", state);
                this.itemWrapper = getBoolProperty("itemWrapper", state);
                this.indexByParent = getStringProperty("indexByParent", state);
                this.loadArrows(state["arrows"]);
                this.angle = getNumberProperty("angle", state);
                this.isLocked = getBoolProperty("isLocked", state, false);
                break;
        }
    }

    fixItemWrappers(reason: string) {
        if (this.isItemWrapper()) {
            this.fixState(reason);
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].fixItemWrappers(reason);
            }
        }
    }

    saveItemWrappers() {
        if (this.isItemWrapper()) {
            this.saveState();
        } else {
            const children = this.children as Group[];

            for (let i = 0; i < children.length; i++) {
                children[i].saveItemWrappers();
            }
        }
    }

    fixState(reason: string) {
        this.fixType = this.getFixType(reason);
        this.fixedState = this.getState(this.fixType);
    }

    saveState() {
        canv.eventManager.createPropertyEvent(
            this.UID,
            this.fixType as FixType,
            this.fixedState,
            this.getState(this.fixType) as EntityBaseState,
        );
    }

    startX = 0;
    startY = 0;
    width = 0;
    height = 0;
    children: (ElementBase | Group)[] = [];
    items: Group[] = [];
    parent: Group | null = null;
    entityType = "group";
    fixedState: any = "";
    needLowOutline = false;
    pointerSize = 0;
    needConnectors = false;
    isGhost = false;
    fixType = "";

    // Сохраняемые поля
    UID = "";
    trailUID = "";
    parentUID = "";
    type = "group";
    itemWrapper = false;
    catchPointer = "";
    angle = 0;
    indexByParent = "a1";
    arrows: ArrowData[] = [];
    isLocked = false;

    anchorGroups: Group[] | null = null;
}
